import { useAnalytics } from '@ecosystem/newron-client-lib';
import React, { Dispatch, SetStateAction } from 'react';
import { useSelector } from 'react-redux';
import { renderVisualization } from '@data-visualization/utilities/ChartUtils';
import PerformanceSegmentChart from '@data-visualization/performance-segment-chart/PerformanceSegmentChart';
import {
  defaultSortingOptions,
  KpiDropdownOption,
  SegmentationChart,
} from '@data-visualization/performance-segment-chart/Types';
import { CategoryLeaderboardPg } from '@models/CategoryLeaderboard';
import { getDisplayNameFromHeaderKey } from '@models/PerformanceAnalysisboard';
import { NoResult } from '@pages/NoResult';
import usePersistedSorting from '@utils/hook/sorting/UsePersistedSorting';
import { useInViewTracking } from '@utils/hook/tracking/UseInViewTracking';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import {
  PAGE_ELEMENT_VIEW_TRACKING_TITLE,
  PageElementViewTrackingParam,
} from '@utils/page-analytics/model/PageElementViewedTracking';
import { FactRevenue, getPageTypeWithPageName, ShareType } from '@utils/pageConfig/Types';
import {
  getCategoryLeaderboardColumns,
  getKPIs,
} from '../category-leaderboard/category-leaderboard-table-columns/CategoryLeaderboardTableColumns';
import { ChartPickers } from '../chartpickers/ChartPickers';
import { GrowthTypes } from '../comparison-growth-selector/Types';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { getInitialAppliedCountries } from '@utils/FilterUtils';
import { IRootState } from '@state/reducers/Reducers';
import { getEffectiveProductType } from '@models/SubscribedProductType';
import { OnExportReadyCallback } from '@ecosystem/export-utils';
import PerformanceSegmentMultipleChart from '@data-visualization/performance-segment-mulitple-chart/PerformanceSegmentMultipleChart';
import { SortingOptions } from '@components/design/migrated/Table/Sorting/types';
import { modifiedCLSegmentsGrandTotalToTotalValue } from '@utils/TotalUtils';
import { FeatureSelectorType } from '@components/feature-selector/Types';
import { getDoubleBarChartComponentName } from './PerformanceSegmentsUtils';
import ErrorResultMessage from '@pages/ErrorResultMessage';

const usePerformanceSegmentsCreateInViewTrackingParam = (
  selectedKpi: KpiDropdownOption,
  volume: string,
  sorting: SortingOptions,
  categoryLeaderboardPg: CategoryLeaderboardPg[],
) => {
  const { track } = useAnalytics();
  const pagesConfig = usePageConfig();
  const sideFiltersState = useSelector((state: IRootState) => state.sideFilters);
  const retailerClientState = useSelector((state: IRootState) => state.retailerClient);
  const mainFiltersStateRO = useSelector((state: IRootState) => state.mainFiltersRO);
  const subscribedProductTypes = useSelector(
    (state: IRootState) => state.subscribedProductTypes.subscribedProductTypes,
  );

  const createParam: () => PageElementViewTrackingParam = () => {
    const effectiveProductType = getEffectiveProductType(subscribedProductTypes, retailerClientState.pageViewing);
    const { pageType, access } = pagesConfig;

    return {
      eventTitle: PAGE_ELEMENT_VIEW_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      retailerClientState: retailerClientState,
      track: track,
      pageType: getPageTypeWithPageName(retailerClientState.pageViewing),
      pageName: retailerClientState.pageViewing,
      componentName: getDoubleBarChartComponentName(pageType),
      elementType: ElementType.BAR_CHART,
      mainFilterState: mainFiltersStateRO,
      elementKpi: getDisplayNameFromHeaderKey(selectedKpi?.value, volume),
      elementSort: sorting,
      hasData: categoryLeaderboardPg.length > 0,
      access,
      initialLocationId: getInitialAppliedCountries(pagesConfig, pageType, effectiveProductType),
    };
  };

  return { createParam };
};

export interface PerformanceSegmentsProps {
  isLoading: boolean;
  categoryLeaderboardPg: CategoryLeaderboardPg[];
  selectedGrowthType: GrowthTypes;
  factRevenue: FactRevenue;
  volume: string;
  onExportReady: OnExportReadyCallback;
  setIsExportButtonDisabled: Dispatch<SetStateAction<boolean>>;
  isUsingCollection?: boolean;
  sortingOptions?: SortingOptions;
  featureSelectorType?: FeatureSelectorType;
  isExplorePage?: boolean;
  shareType?: ShareType;
  isApiError?: boolean;
}

const PerformanceSegments: React.FC<PerformanceSegmentsProps> = ({
  isLoading,
  categoryLeaderboardPg,
  selectedGrowthType,
  factRevenue,
  volume,
  onExportReady,
  setIsExportButtonDisabled,
  isUsingCollection = false,
  sortingOptions,
  featureSelectorType,
  isExplorePage,
  shareType = ShareType.MarketShare,
  isApiError,
}) => {
  const productGroupsKpi = categoryLeaderboardPg.slice(0, 20);

  const modifiedProductGroupsKpi = modifiedCLSegmentsGrandTotalToTotalValue(productGroupsKpi);

  const { sorting: pagesConfigSorting, updateOptions } = usePersistedSorting({
    tableId: SegmentationChart,
    defaultSorting: defaultSortingOptions,
  });

  const sorting = sortingOptions ?? pagesConfigSorting;

  const kpiData = getCategoryLeaderboardColumns(shareType, volume);

  const kpiSelectionOptions = getKPIs(kpiData);

  const selectedKpi =
    kpiSelectionOptions.find((option) => option.value === sorting.headerKey) ?? kpiSelectionOptions[0];

  const { createParam } = usePerformanceSegmentsCreateInViewTrackingParam(
    selectedKpi,
    volume,
    sorting,
    categoryLeaderboardPg,
  );

  const { ref } = useInViewTracking(createParam, { threshold: 0.2, triggerOnce: true });

  return (
    <>
      {!isUsingCollection && !isExplorePage && (
        <ChartPickers
          sorting={sorting}
          updateSorting={updateOptions}
          kpisConfig={kpiData}
          getDisplayName={getDisplayNameFromHeaderKey}
          factRevenue={factRevenue}
          volume={volume}
          featureSelectorType={featureSelectorType}
          isLoading={isLoading}
        />
      )}
      {renderVisualization({
        isLoading: isLoading,
        fetchedApiData: modifiedProductGroupsKpi,
        defaultData: [],
        jsxToRender: (
          <div ref={isUsingCollection ? null : ref}>
            <PerformanceSegmentChart
              categoryLeaderboardPg={modifiedProductGroupsKpi}
              selectedGrowthType={selectedGrowthType}
              selectedKpi={selectedKpi}
              factRevenue={factRevenue}
              onExportReady={onExportReady}
            />
            <PerformanceSegmentMultipleChart
              categoryLeaderboardPg={modifiedProductGroupsKpi}
              selectedGrowthType={selectedGrowthType}
              selectedKpi={selectedKpi}
              factRevenue={factRevenue}
            />
          </div>
        ),
        noResultHandler: () => setIsExportButtonDisabled(true),
        noResultJsx: <div ref={isUsingCollection ? null : ref}>{NoResult()}</div>,
        errorMessageJsx: <div ref={isUsingCollection ? null : ref}>{ErrorResultMessage()}</div>,
        isError: isApiError,
      })}
    </>
  );
};

export default PerformanceSegments;
